import type { ReactNode } from "react";
import { lazy, Suspense, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { HelmetProvider } from "react-helmet-async";
import appCss from "../styles.css?url";
import { OSWALD_FONT_URL, SOURCE_SANS_FONT_URL } from "@/lib/fontAssets";
import { TooltipProvider } from "@/components/ui/tooltip";
import { PartsCartProvider } from "@/context/PartsCartContext";
import { SiteThemeProvider } from "@/components/SiteThemeProvider";
import { getInitialSiteTheme } from "@/lib/siteTheme.functions";
import { CARMAX_LOGO_SRCSET, CARMAX_LOGO_SIZES, STATIC_ASSETS } from "@/lib/imageUtils";
import InteractionGate, { useInteractionReady } from "@/components/ui/InteractionGate";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import {
  installChunkErrorRecovery,
  isChunkLoadError,
  recoverFromChunkError,
} from "@/lib/chunk-recovery";
import {
  ORG_JSONLD,
  MANIFEST_LOADER_SCRIPT,
  HERO_PRELOAD_SCRIPT,
  GTM_SCRIPT,
  ADMIN_AUTH_BOOT_GUARD_SCRIPT,
} from "@/lib/boot-scripts";

import NotFound from "@/pages/NotFound";

const PartsCatalogTopBar = lazy(() => import("@/components/parts/PartsCatalogTopBar"));
const FloatingCartButton = lazy(() => import("@/components/parts/FloatingCartButton"));
const RadixToaster = lazy(() => import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })));
const SonnerToaster = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));
const SiteErrorTracker = lazy(() => import("@/components/analytics/SiteErrorTracker"));
const PageTracker = lazy(() => import("@/components/analytics/PageTracker"));
const RedirectHandler = lazy(() => import("@/components/RedirectHandler"));
const PopupForm = lazy(() => import("@/components/PopupForm"));
const HolidayPopup = lazy(() => import("@/components/HolidayPopup"));
const HeadScripts = lazy(() => import("@/components/HeadScripts"));
const ThemePreviewInjector = lazy(() => import("@/components/ThemePreviewInjector"));

// Loads the theme layer after mount: admin previews, plus the live
// schedule / override (which injects nothing while Classic is live).
function ThemePreviewGate() {
  const [on, setOn] = useState(false);
  useEffect(() => setOn(true), []);
  if (!on) return null;
  return (
    <Suspense fallback={null}>
      <ThemePreviewInjector />
    </Suspense>
  );
}

// Ported from App.tsx: react-router ScrollToTop — every navigation resets
// scroll to the top of the page.
function ScrollToTop() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, [pathname]);
  return null;
}

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const engaged = useInteractionReady();
  useEffect(() => {
    document.documentElement.toggleAttribute("data-user-engaged", engaged);
  }, [engaged]);
  const { queryClient } = Route.useRouteContext();
  const { initialTheme } = Route.useLoaderData();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  useEffect(() => {
    installChunkErrorRecovery();
    // Signal committed hydration; direct analytics can now use browser idle.
    window.__rexAppHydrated = true;
    window.dispatchEvent(new Event("rex-app-hydrated"));
  }, []);
  const isPartsCatalog = pathname.startsWith("/parts-catalog");
  const isPartsCatalogTest = (pathname === "/parts-preview" || pathname === "/parts-catalog-test");
  // The CarMax theme's parts landing renders its own chrome; Classic keeps the shared top bar.
  const isCarmaxPartsLanding = pathname === "/parts-catalog" && initialTheme?.layout === "carmax";
  // Only the parts storefront consumes the cart context (AddToCartButton,
  // FloatingCartButton, PartsCatalogTopBar, PartsCatalogCart/Checkout — all
  // under /parts-catalog). Mounting the provider sitewide made every page —
  // including the homepage — pay for its localStorage read and its Supabase
  // lazy-load path during hydration. /producttag is included as a margin
  // since those pages link into the storefront.
  const needsCart = isPartsCatalog || pathname.startsWith("/producttag");

  const shell = (
    <>
      <ScrollToTop />
      {isPartsCatalog && !isPartsCatalogTest && !isCarmaxPartsLanding && (
        <Suspense fallback={null}>
          <PartsCatalogTopBar />
        </Suspense>
      )}
      <Suspense fallback={null}>
        <PageTracker />
        {/* Backstop for the server-side 301s in src/lib/server-redirects.ts;
            self-defers to idle internally so LCP is unaffected. */}
        <RedirectHandler />
      </Suspense>
      <Outlet />
      <ThemePreviewGate />
      {isPartsCatalog && (
        <Suspense fallback={null}>
          <FloatingCartButton />
        </Suspense>
      )}
      {/* Engagement-only: no idle timer, so a no-interaction load
          (Lighthouse) never mounts these chunks. */}
      <InteractionGate>
        <Suspense fallback={null}>
          <RadixToaster />
          <SonnerToaster />
          <SiteErrorTracker />

          <PopupForm />
          <HolidayPopup />
          <HeadScripts />
        </Suspense>
      </InteractionGate>
    </>
  );

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <SiteThemeProvider initialTheme={initialTheme}>
            {needsCart ? <PartsCartProvider>{shell}</PartsCartProvider> : shell}
          </SiteThemeProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

function RootErrorComponent({ error, reset }: { error: Error; reset: () => void; info?: unknown }) {
  const router = useRouter();
  const staleAssets = isChunkLoadError(error);
  useEffect(() => {
    if (staleAssets) {
      // A deploy replaced the hashed chunks this tab was holding. Clear caches
      // and reload instead of showing an error the user can't act on.
      void recoverFromChunkError().then((recovering) => {
        if (!recovering) {
          router.invalidate();
          reset();
        }
      });
      return;
    }
    console.error(error);
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error, staleAssets, router, reset]);

  if (staleAssets) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6 text-center text-foreground">
        <p className="text-muted-foreground">Loading the latest version&hellip;</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6 text-center text-foreground">
      <h1 className="font-heading text-3xl font-bold">This page didn&rsquo;t load</h1>
      <p className="mt-3 max-w-md text-muted-foreground">
        Something went wrong on our end. You can try again, or head back to the homepage.
      </p>
      <div className="mt-6 flex gap-3">
        <button
          type="button"
          onClick={() => {
            router.invalidate();
            reset();
          }}
          className="rounded-sm bg-primary px-5 py-2.5 font-heading text-sm font-semibold uppercase tracking-wide text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Try again
        </button>
        <a
          href="/"
          className="rounded-sm border border-border bg-card px-5 py-2.5 font-heading text-sm font-semibold uppercase tracking-wide text-foreground transition-colors hover:bg-muted"
        >
          Go home
        </a>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  loader: async () => ({ initialTheme: await getInitialSiteTheme() }),
  head: ({ loaderData, matches }) => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0, viewport-fit=cover" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "theme-color", content: "#b71c1c" },
      // No sitewide title/description here: TanStack concatenates meta arrays,
      // so root entries duplicated onto every leaf page (SEO finding). Each
      // leaf route declares its own unique title and description.
      { name: "author", content: "Rex & Sons RVs Inc" },
      // Social preview tags (og:type, og:title, og:description, og:image,
      // twitter:card, twitter:image, ...) are set per leaf route — TanStack
      // concatenates meta arrays, so root entries duplicated on every page.
      { name: "twitter:site", content: "@RexAndSonsRVs" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      // Only the homepage is likely to request CDN graphics during initial load.
      ...(matches.some((match) => String(match.routeId) === "/")
        ? [{ rel: "preconnect", href: "https://www.myrvpart.com", crossOrigin: "anonymous" as const }]
        : []),
      // Same hashed files styles.css bundles, so the preload is reused.
      { rel: "preload", href: OSWALD_FONT_URL, as: "font", type: "font/woff2", crossOrigin: "anonymous" },
      { rel: "preload", href: SOURCE_SANS_FONT_URL, as: "font", type: "font/woff2", crossOrigin: "anonymous" },
      // The header logo is the LCP element on most routes. Preload the single
      // URL the header renders (logo-400, no srcset) so the request is
      // discoverable in the initial document and matches the <img> exactly —
      // a responsive srcset preload risked a second, unused download.
      {
        rel: "preload",
        as: "image",
        type: "image/webp",
        // The logo is served same-origin (no CDN) so no third-party cookies
        // are set on the homepage — myrvpart.com set fpestid on its copy.
        href: STATIC_ASSETS.logo400,
        // Modern non-home pages render a responsive logo; mirror its set.
        ...(loaderData?.initialTheme?.layout === "carmax" && !matches.some((match) => String(match.routeId) === "/")
          ? { imageSrcSet: CARMAX_LOGO_SRCSET, imageSizes: CARMAX_LOGO_SIZES }
          : {}),
        // On unit pages the RV photo is the LCP element and already carries a
        // high-priority preload; rushing the logo too would split the narrow
        // mobile connection, so the logo yields there.
        fetchPriority: matches.some((match) => String(match.routeId).startsWith("/rv-search"))
          ? ("low" as const)
          : ("high" as const),
      },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon-180.png" },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/favicon-512.png" },
      { rel: "preconnect", href: "https://cpkvmigljcrjxctoixoq.supabase.co", crossOrigin: "anonymous" },
      // Inlined PWA manifest: removes a blocking same-origin request (~0.9 s
      // chain on mobile) and the platform serves the static file as no-cache.
      {
        rel: "manifest",
        href:
          "data:application/manifest+json," +
          encodeURIComponent(
            '{"name":"Rex and Sons RVs","short_name":"Rex & Sons","description":"Rex and Sons RVs - Wilmington, NC RV dealer for campers, travel trailers, fifth wheels, parts and service.","theme_color":"#dc2626","background_color":"#ffffff","display":"standalone","start_url":"/","scope":"/","icons":[{"src":"/favicon-32.png","sizes":"32x32","type":"image/png"},{"src":"/apple-touch-icon-180.png","sizes":"180x180","type":"image/png","purpose":"any"},{"src":"/favicon-512.png","sizes":"512x512","type":"image/png","purpose":"any maskable"}]}',
          ),
      },
      // NOTE: no sitewide canonical here. TanStack concatenates `links`, so a
      // root canonical shipped a second (homepage) canonical on every page,
      // which is what Google reported as "Duplicate without user-selected
      // canonical". Each leaf route declares its own self-canonical.
    ],
    scripts: [
      { children: GTM_SCRIPT },
      { type: "application/ld+json", children: ORG_JSONLD },
      { children: MANIFEST_LOADER_SCRIPT },
      { children: HERO_PRELOAD_SCRIPT },
      { children: ADMIN_AUTH_BOOT_GUARD_SCRIPT },
    ],

  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: RootErrorComponent as never,
});